import React, {
  ChangeEvent,
  InputHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from "react"
import styled, { css } from "styled-components"
import LoupeIcon from "../../../images/icons/loupe.inline.svg"
import DeleteNarrowIcon from "../../../images/icons/delete-narrow.inline.svg"
import { AppFunctionComponent } from "../../../types"

const iconStyle = css`
  position: absolute;
  top: 50%;
  margin-top: -8px;
  width: 16px;
  height: 16px;
  pointer-events: none;
`

const InputLoupeIcon = styled(LoupeIcon)`
  ${iconStyle};
  left: 0;
`

const InputRemoveIcon = styled(DeleteNarrowIcon)<{ $active: boolean }>`
  ${iconStyle};
  right: 0;
  opacity: 0;
  transition: opacity 0.2s linear;
  ${({ $active }) =>
    $active &&
    css`
      pointer-events: auto;
      cursor: pointer;
      opacity: 1;
    `}

  circle {
    transition: fill 0.5s;
    fill: ${({ theme }) => theme.color.inputBorder};

    &:hover {
      fill: ${({ theme }) => theme.color.inputBorderActive};
    }
  }

  g > g {
    transition: stoke 0.5s;
    stroke: ${({ theme }) => theme.color.removeIconStroke};

    &:hover {
      stroke: ${({ theme }) => theme.color.removeIconStrokeHover};
    }
  }
`

const InputElement = styled.input`
  width: 100%;
  padding: 5px 20px 5px 36px;
  box-sizing: border-box;
  background: transparent;

  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.color.inputBorder};
  border-radius: 0;

  font-size: 1rem;
  font-weight: 300;
  line-height: 2em;
  font-family: ${({ theme }) => theme.font.basic};

  transition: border-color 0.5s;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.color.inputBorderActive};
  }

  &::placeholder {
    color: ${({ theme }) => theme.color.text};
  }
`

const InputWrapper = styled.div`
  position: relative;

  &:hover ${InputElement} {
    border-color: ${({ theme }) => theme.color.inputBorderActive};
  }
`

export interface Props extends InputHTMLAttributes<HTMLInputElement> {
  defaultValue?: string
  onValueChange: (value: string) => void
  label?: string
}

const Input: AppFunctionComponent<Props> = ({
  defaultValue = "",
  onValueChange,
  label,
  ...props
}) => {
  const ref = useRef<HTMLInputElement>(null)
  const [value, setValue] = useState(defaultValue)

  useEffect(() => setValue(defaultValue), [defaultValue])

  useEffect(() => onValueChange(value), [value])

  const updateValue = ({ target }: ChangeEvent<HTMLInputElement>) => {
    setValue(target.value)
  }

  const clearValue = () => {
    setValue("")
    ref.current?.focus()
  }

  return (
    <InputWrapper>
      <InputLoupeIcon />
      <InputRemoveIcon $active={Boolean(value)} onClick={clearValue} />
      <InputElement
        ref={ref}
        role="textbox"
        onChange={updateValue}
        value={value}
        aria-label={label}
        {...props}
      />
    </InputWrapper>
  )
}

export default Input
